import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import EmergencyWarning from "@/components/EmergencyWarning";
import { Baby, MessageCircle, Phone, Video, Sparkles } from "lucide-react";

const CONCERNS = [
  "Newborn care", "Feeding problem", "Low milk", "Vomiting", "Gas/colic",
  "Wet diapers", "Stool / loose motion / constipation", "Fever", "Cough/cold",
  "Sleep", "Growth", "Nutrition", "Vaccines", "Rash", "Eye discharge",
  "Crying/fussiness", "Development / milestones", "Other",
];

const CONSULT_TYPES = [
  { key: "chat", label: "Chat", icon: MessageCircle, sub: "Text messages, photos & reports" },
  { key: "audio", label: "Audio Call", icon: Phone, sub: "Voice consultation via Jitsi" },
  { key: "video", label: "Video Call", icon: Video, sub: "Face-to-face via Jitsi Meet" },
];

export default function BookConsult() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [emergencyAck, setEmergencyAck] = useState(false);
  const [children, setChildren] = useState([]);
  const [showAddChild, setShowAddChild] = useState(false);
  const [child, setChild] = useState({ name: "", dob: "", sex: "male", current_weight_kg: "", feeding_type: "" });
  const [form, setForm] = useState({
    child_id: "", consultation_type: "video", concern_category: "Newborn care",
    duration: "", fever_temp: "", urine_count: "", stool_pattern: "", vomiting: "", breathing: "no", activity: "normal",
    preferred_datetime: "", consent: false, notes: "",
  });
  const [triage, setTriage] = useState(null);
  const [triageLoading, setTriageLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [settings, setSettings] = useState({ upi_id: "", consultation_fee: 300 });
  const [appointment, setAppointment] = useState(null);
  const [screenshot, setScreenshot] = useState(null);

  useEffect(() => {
    if (!user) { nav("/login", { state: { from: "/book" } }); return; }
    api.get("/children").then((r) => setChildren(r.data));
    api.get("/settings/public").then((r) => setSettings(r.data));
  }, [user, nav]);

  const saveChild = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/children", { ...child, current_weight_kg: child.current_weight_kg ? Number(child.current_weight_kg) : null });
    setChildren([...children, data]);
    setForm({ ...form, child_id: data.id });
    setShowAddChild(false);
  };

  const runTriage = async () => {
    setTriageLoading(true);
    try {
      const { data } = await api.post("/ai/triage", {
        symptoms: `Concern: ${form.concern_category}. Details: ${form.notes || "None"}. Vomiting: ${form.vomiting || "no"}. Breathing: ${form.breathing}. Activity: ${form.activity}.`,
        fever_temp: form.fever_temp ? Number(form.fever_temp) : null,
        duration: form.duration,
      });
      setTriage(data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
    finally { setTriageLoading(false); }
  };

  const bookNow = async () => {
    setErr(null);
    try {
      const { data } = await api.post("/appointments", {
        child_id: form.child_id,
        consultation_type: form.consultation_type,
        concern_category: form.concern_category,
        symptoms: {
          duration: form.duration, fever_temp: form.fever_temp, urine_count: form.urine_count,
          stool_pattern: form.stool_pattern, vomiting: form.vomiting, breathing: form.breathing,
          activity: form.activity, notes: form.notes,
        },
        preferred_datetime: form.preferred_datetime || new Date().toISOString(),
        consent: true, emergency_ack: true,
      });
      setAppointment(data);
      setStep(4);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail)); }
  };

  const fileToDataUrl = (f) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });

  const submitPayment = async () => {
    if (!screenshot) return alert("Please upload the payment screenshot.");
    const dataUrl = await fileToDataUrl(screenshot);
    await api.post(`/appointments/${appointment.id}/payment`, { payment_screenshot: dataUrl });
    setStep(5);
  };

  if (!user) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="book-page">
      <div className="overline mb-1">Teleconsultation</div>
      <h1 className="font-display font-black text-3xl sm:text-4xl mb-6" style={{color:"#232F2F"}}>Book with Dr. Hariom</h1>

      {/* Stepper */}
      <div className="flex items-center gap-2 mb-6 text-xs font-semibold">
        {[1,2,3,4,5].map((s) => (
          <div key={s} className={`flex-1 h-1.5 rounded-full ${step>=s ? 'bg-[#2D7A78]' : 'bg-[#E5EBEB]'}`}/>
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-5">
          <EmergencyWarning ack={emergencyAck} onAck={setEmergencyAck}/>
          <button disabled={!emergencyAck} onClick={() => setStep(2)} className="btn-primary" data-testid="continue-after-warning">Continue</button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <div className="card-soft">
            <h3 className="font-display font-bold text-lg mb-3">Select child profile</h3>
            {children.length === 0 && <div className="text-sm text-[#5A6D6D] mb-3">You don't have a child profile yet. Please add one.</div>}
            <div className="grid sm:grid-cols-2 gap-3">
              {children.map((c) => (
                <button key={c.id} onClick={() => setForm({...form, child_id: c.id})} className={`text-left p-4 rounded-2xl border ${form.child_id===c.id ? 'border-[#2D7A78] bg-[#CBE4E8]/40' : 'border-[#E5EBEB]'}`} data-testid={`child-select-${c.id}`}>
                  <div className="flex items-center gap-2"><Baby size={18} color="#2D7A78"/><div className="font-semibold">{c.name}</div></div>
                  <div className="text-xs text-[#5A6D6D] mt-1">{c.sex} · DOB {c.dob} {c.current_weight_kg && `· ${c.current_weight_kg} kg`}</div>
                </button>
              ))}
            </div>
            {!showAddChild ? (
              <button onClick={() => setShowAddChild(true)} className="mt-3 text-sm text-[#2D7A78] font-semibold" data-testid="add-child-btn">+ Add child profile</button>
            ) : (
              <form onSubmit={saveChild} className="mt-4 space-y-3 border-t border-[#E5EBEB] pt-4">
                <input required placeholder="Child name" className="w-full px-3 py-2 rounded-xl border border-[#E5EBEB]" value={child.name} onChange={(e)=>setChild({...child, name: e.target.value})} data-testid="child-name"/>
                <div className="grid grid-cols-2 gap-3">
                  <input required type="date" className="px-3 py-2 rounded-xl border border-[#E5EBEB]" value={child.dob} onChange={(e)=>setChild({...child, dob: e.target.value})} data-testid="child-dob"/>
                  <select className="px-3 py-2 rounded-xl border border-[#E5EBEB]" value={child.sex} onChange={(e)=>setChild({...child, sex: e.target.value})} data-testid="child-sex">
                    <option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input placeholder="Weight (kg)" type="number" step="0.01" className="px-3 py-2 rounded-xl border border-[#E5EBEB]" value={child.current_weight_kg} onChange={(e)=>setChild({...child, current_weight_kg: e.target.value})} data-testid="child-weight"/>
                  <select className="px-3 py-2 rounded-xl border border-[#E5EBEB]" value={child.feeding_type} onChange={(e)=>setChild({...child, feeding_type: e.target.value})}>
                    <option value="">Feeding type</option>
                    <option value="breastfeeding">Breastfeeding</option>
                    <option value="formula">Formula</option>
                    <option value="mixed">Mixed</option>
                    <option value="complementary">Complementary</option>
                  </select>
                </div>
                <button className="btn-primary" data-testid="save-child-btn">Save child</button>
              </form>
            )}
          </div>
          <button disabled={!form.child_id} onClick={() => setStep(3)} className="btn-primary" data-testid="continue-to-symptoms">Continue</button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-5">
          <div className="card-soft">
            <h3 className="font-display font-bold text-lg mb-3">Consultation type</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {CONSULT_TYPES.map((c) => {
                const Icon = c.icon;
                return (
                  <button key={c.key} onClick={() => setForm({...form, consultation_type: c.key})} className={`text-left p-4 rounded-2xl border ${form.consultation_type===c.key ? 'border-[#2D7A78] bg-[#CBE4E8]/40' : 'border-[#E5EBEB]'}`} data-testid={`consult-type-${c.key}`}>
                    <Icon size={20} color="#2D7A78"/>
                    <div className="font-semibold mt-2">{c.label}</div>
                    <div className="text-xs text-[#5A6D6D]">{c.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="card-soft">
            <h3 className="font-display font-bold text-lg mb-3">Symptom details</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#5A6D6D]">CONCERN CATEGORY</label>
                <select value={form.concern_category} onChange={(e)=>setForm({...form, concern_category: e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5EBEB]" data-testid="concern-category">
                  {CONCERNS.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A6D6D]">DURATION</label>
                <input placeholder="e.g. 2 days" value={form.duration} onChange={(e)=>setForm({...form, duration: e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5EBEB]" data-testid="symptom-duration"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A6D6D]">FEVER (°C)</label>
                <input type="number" step="0.1" value={form.fever_temp} onChange={(e)=>setForm({...form, fever_temp: e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5EBEB]" data-testid="symptom-fever"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A6D6D]">URINE COUNT (24H)</label>
                <input type="number" value={form.urine_count} onChange={(e)=>setForm({...form, urine_count: e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5EBEB]" data-testid="symptom-urine"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A6D6D]">STOOL PATTERN</label>
                <input placeholder="normal / loose / hard" value={form.stool_pattern} onChange={(e)=>setForm({...form, stool_pattern: e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5EBEB]" data-testid="symptom-stool"/>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#5A6D6D]">VOMITING</label>
                <input placeholder="none / occasional / frequent" value={form.vomiting} onChange={(e)=>setForm({...form, vomiting: e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5EBEB]" data-testid="symptom-vomiting"/>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-[#5A6D6D]">PREFERRED DATE/TIME</label>
                <input type="datetime-local" value={form.preferred_datetime} onChange={(e)=>setForm({...form, preferred_datetime: e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5EBEB]" data-testid="preferred-datetime"/>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-[#5A6D6D]">NOTES / DESCRIPTION</label>
                <textarea rows={3} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E5EBEB]" data-testid="symptom-notes"/>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={runTriage} disabled={triageLoading} className="btn-outline text-sm" data-testid="ai-triage-btn">
                <Sparkles size={16}/> {triageLoading ? "Analysing..." : "AI Urgency Check"}
              </button>
            </div>
            {triage && (
              <div className="mt-4 p-4 rounded-xl" style={{background: triage.urgency==="emergency"?"#FFE2E2":"#F0F7F7", border:"1px solid #E5EBEB"}} data-testid="triage-result">
                <div className="text-sm font-bold uppercase" style={{color: triage.urgency==="emergency"?"#B3261E":"#2D7A78"}}>Urgency: {triage.urgency.replace("_"," ")}</div>
                <p className="text-sm mt-2">{triage.guidance}</p>
                {triage.red_flags?.length > 0 && (
                  <ul className="mt-2 list-disc pl-5 text-xs text-[#5A6D6D]">{triage.red_flags.map((r,i)=><li key={i}>{r}</li>)}</ul>
                )}
                <div className="text-[11px] mt-2 text-[#5A6D6D]">{triage.disclaimer}</div>
              </div>
            )}
          </div>

          <div className="card-soft">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input required type="checkbox" checked={form.consent} onChange={(e)=>setForm({...form, consent: e.target.checked})} className="mt-1 accent-[#2D7A78]" data-testid="consent-checkbox"/>
              <span>I confirm I am the parent/legal guardian of the child. I consent to teleconsultation with Dr. Hariom Choudhary. I understand that teleconsultation has limitations and physical examination may be advised when needed.</span>
            </label>
          </div>

          {err && <div className="text-sm text-[#B3261E] bg-[#FFE2E2] p-3 rounded-lg" data-testid="book-error">{err}</div>}
          <button disabled={!form.consent} onClick={bookNow} className="btn-primary" data-testid="submit-booking">Continue to payment</button>
        </div>
      )}

      {step === 4 && appointment && (
        <div className="card-soft space-y-4" data-testid="payment-step">
          <h3 className="font-display font-bold text-xl">Payment — ₹{appointment.fee}</h3>
          <p className="text-sm text-[#5A6D6D]">Pay via UPI to <span className="font-bold text-[#232F2F]">{settings.upi_id}</span>. Then upload your payment screenshot below. Admin will verify and confirm your consultation.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-[#F0F7F7]">
              <div className="text-xs font-semibold uppercase text-[#5A6D6D]">UPI ID</div>
              <div className="font-mono font-bold text-lg mt-1">{settings.upi_id}</div>
              <div className="text-xs mt-2 text-[#5A6D6D]">Any UPI app (PhonePe / Google Pay / Paytm / BHIM)</div>
            </div>
            <div className="p-4 rounded-xl bg-[#F0F7F7] flex items-center justify-center">
              <img alt="qr" src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=upi://pay?pa=${encodeURIComponent(settings.upi_id)}&am=${appointment.fee}&cu=INR`}/>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5A6D6D]">UPLOAD SCREENSHOT</label>
            <input type="file" accept="image/*" onChange={(e)=>setScreenshot(e.target.files?.[0])} className="block mt-1" data-testid="payment-screenshot"/>
          </div>
          <button onClick={submitPayment} className="btn-primary" data-testid="submit-payment">Submit for review</button>
        </div>
      )}

      {step === 5 && (
        <div className="card-soft text-center" data-testid="booking-success">
          <div className="text-4xl">✅</div>
          <h3 className="font-display font-bold text-xl mt-2">Payment submitted</h3>
          <p className="text-sm text-[#5A6D6D] mt-2">Dr. Hariom's team will verify and confirm shortly. Check your parent dashboard.</p>
          <button onClick={() => nav("/dashboard")} className="btn-primary mt-4">Go to Dashboard</button>
        </div>
      )}
    </div>
  );
}
